import { Card, Col, Form, Input, Row, Select, Button, Tag, Space, Typography, Alert, Tooltip } from "antd";
import React, { useState, useEffect } from "react";
import { useAddressHelper } from "utils/useAddressHelper";
import { PlusOutlined } from "@ant-design/icons";
import { PartialJobInterface, JobLocation } from "api/jobs/dto";
import { numberValidator } from "utils/number-validator";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../firebase/firebase";

interface LanguageType {
  id: string;
  name: string;
}

const { Option } = Select;

type Props = {
  formData: PartialJobInterface;
  setFormData: React.Dispatch<React.SetStateAction<PartialJobInterface>>;
};

const { Text } = Typography;

const VacancyLocationCard: React.FC<Props> = ({ formData, setFormData }) => {
  const [form] = Form.useForm();
  const { getProvinceOptions, getDistrictOptions } = useAddressHelper();
  const [languages, setLanguages] = useState<LanguageType[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDistrict, setSelectedDistrict] = useState<string | undefined>(undefined);
  const [bulkLanguages, setBulkLanguages] = useState<string[]>([]);
  const [newSkill, setNewSkill] = useState<string>("");

  const handleFormChange = (changedValues: any, allValues: any) => {
    setFormData((prev) => ({ ...prev, ...allValues }));
  };

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "enumerator_languages"));
        const languageList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name,
        }));
        setLanguages(languageList);
      } catch (error) {
        console.error("Error fetching languages:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchLanguages();
  }, []);

  const handleAddSkill = () => {
    if (newSkill) {
      const currentSkills = form.getFieldValue("skills") || [];
      const updatedSkills = [...currentSkills, newSkill];
      form.setFieldsValue({ skills: updatedSkills });
      setNewSkill("");
      setFormData((prev) => ({ ...prev, skills: updatedSkills }));
    }
  };

  const handleRemoveSkill = (skill: string) => {
    const currentSkills = form.getFieldValue("skills") || [];
    const updatedSkills = currentSkills.filter((s: string) => s !== skill);
    form.setFieldsValue({ skills: updatedSkills });
    setFormData((prev) => ({ ...prev, skills: updatedSkills }));
  };

  const districtOptions = getDistrictOptions();

  const buildJobLocation = (districtLabel: string): JobLocation | null => {
    const selectedDistrictObj = districtOptions.find((d) => d.label === districtLabel);
    if (!selectedDistrictObj) return null;
    const districtData = JSON.parse(selectedDistrictObj.value as string);
    const provinceObj = getProvinceOptions().find((p) => p.key === districtData.province_id);
    if (!provinceObj) return null;
    return {
      district: districtLabel,
      province: provinceObj.label,
      languages: []
    };
  };

  const handleAddJobLocation = () => {
    if (selectedDistrict) {
      const newLocation = buildJobLocation(selectedDistrict);
      if (newLocation) {
        const currentLocations = form.getFieldValue("joblocation") || [];
        const updatedLocations = [...currentLocations, newLocation];
        form.setFieldsValue({ joblocation: updatedLocations });
        setSelectedDistrict(undefined);
        setFormData((prev) => ({ ...prev, joblocation: updatedLocations }));
      }
    }
  };

  const handleAddAllDistricts = () => {
    const currentLocations: JobLocation[] = form.getFieldValue("joblocation") || [];
    const existingDistricts = new Set(currentLocations.map((loc) => loc.district));

    const newLocations = districtOptions
      .filter((d) => !existingDistricts.has(d.label))
      .map((d) => buildJobLocation(d.label))
      .filter((loc): loc is JobLocation => Boolean(loc));

    if (newLocations.length) {
      const updatedLocations = [...currentLocations, ...newLocations];
      form.setFieldsValue({ joblocation: updatedLocations });
      setFormData((prev) => ({ ...prev, joblocation: updatedLocations }));
    }
    setSelectedDistrict(undefined);
  };

  const handleClearAllDistricts = () => {
    form.setFieldsValue({ joblocation: [] });
    setFormData((prev) => ({ ...prev, joblocation: [] }));
    setSelectedDistrict(undefined);
  };

  const handleApplyLanguagesToAll = () => {
    const currentLocations: JobLocation[] = form.getFieldValue("joblocation") || [];
    if (!currentLocations.length) return;
    const updatedLocations = currentLocations.map((loc) => ({
      ...loc,
      languages: bulkLanguages,
    }));
    form.setFieldsValue({ joblocation: updatedLocations });
    setFormData((prev) => ({ ...prev, joblocation: updatedLocations }));
  };


  const handleRemoveJobLocation = (district: string) => {
    const currentLocations = form.getFieldValue("joblocation") || [];
    const updatedLocations = currentLocations.filter((loc: JobLocation) => loc.district !== district);
    form.setFieldsValue({ joblocation: updatedLocations });
    setFormData((prev) => ({ ...prev, joblocation: updatedLocations }));
  };

  return (
    <Form
      key={formData?.id}
      form={form}
      layout="vertical"
      initialValues={{
        numberOfVacancy: formData?.numberOfVacancy || "",
        skills: formData?.skills || [],
        joblocation: formData?.joblocation || []
      }}
      onValuesChange={handleFormChange}
    >
      <Card className="mx-5 shadow-md" size="small" title="Vacancy and Location Details">
        <Row gutter={[48, 32]}>
          <Col xs={24} sm={12}>
            <Form.Item name="numberOfVacancy" label="Number of Vacancy" rules={[{ validator: numberValidator }]}>
              <Input placeholder="eg. 21" />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Card title="Skills" size="small">
              <Row gutter={[16, 16]}>
                <Col xs={24}>
                  <Input
                    placeholder="Add a skill"
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    onPressEnter={handleAddSkill}
                    suffix={
                      <Button type="primary" icon={<PlusOutlined />} onClick={handleAddSkill} size="small" />
                    }
                  />
                </Col>
                <Col xs={24}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {form.getFieldValue("skills")?.map((skill: string) => (
                      <Tag key={skill} closable onClose={() => handleRemoveSkill(skill)} style={{ marginBottom: "8px" }}>
                        {skill}
                      </Tag>
                    ))}
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24}>
            <Card title="Location Details" size="small">
              <Row gutter={16}  >
                <Col xs={24} sm={18}  >
                  <Form.Item >
                    <Select
                      allowClear
                      showSearch
                      placeholder="Select district"
                      optionFilterProp="children"
                      value={selectedDistrict}
                      onChange={(value) => {
                        if (value === "__ALL_DISTRICTS__") {
                          handleAddAllDistricts();
                          return;
                        }
                        setSelectedDistrict(value);
                      }}
                      className="w-full "
                    >
                      <Option
                        key="__ALL_DISTRICTS__"
                        value="__ALL_DISTRICTS__"
                        disabled={
                          (form.getFieldValue("joblocation")?.length || 0) >= districtOptions.length
                        }
                      >
                        Select all districts
                      </Option>
                      {districtOptions?.map((district) => {
                        const isSelected = form.getFieldValue("joblocation")?.some(
                          (loc: JobLocation) => loc.district === district.label
                        );
                        return (
                          <Option key={district.label} value={district.label} disabled={isSelected}>
                            {district.label}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={6} >
                  <Form.Item >
                    <Button type="primary" block onClick={handleAddJobLocation} disabled={!selectedDistrict}>
                      Add
                    </Button>
                  </Form.Item>
                </Col>
                <Col xs={24} >
                  <Space style={{ marginBottom: 12, width: "100%", justifyContent: "space-between" }}>
                    <Text type="secondary">
                      Selected districts: {form.getFieldValue("joblocation")?.length || 0} / {districtOptions.length}
                    </Text>
                    <Space>
                      <Tooltip title="Add every remaining district in one click">
                        <Button size="small" onClick={handleAddAllDistricts}>
                          Select all districts
                        </Button>
                      </Tooltip>
                      <Tooltip title="Remove all selected districts">
                        <Button size="small" danger onClick={handleClearAllDistricts}>
                          Clear all
                        </Button>
                      </Tooltip>
                    </Space>
                  </Space>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }} >
                    {form.getFieldValue("joblocation")?.map((loc: JobLocation) => (
                      <Tag
                        key={`${loc.district}-${loc.province}`}
                        closable
                        color="blue"
                        onClose={() => handleRemoveJobLocation(loc.district)}
                        style={{ marginBottom: "8px" }}
                      >
                        {`${loc.district}${loc.province ? `, ${loc.province}` : ""}`}
                      </Tag>
                    ))}
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24}>
            <Card title="District Languages" size="small">
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="Tip: choose languages below and click Apply to all districts to bulk-assign. You can still tweak languages per district afterwards."
              />
              <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
                <Col xs={24} sm={18}>
                  <Select
                    mode="tags"
                    style={{ width: "100%" }}
                    placeholder={loading ? "Loading..." : "Select or add languages for all districts"}
                    value={bulkLanguages}
                    onChange={setBulkLanguages}
                  >
                    {languages.map((lang) => (
                      <Option key={lang.id} value={lang.name}>
                        {lang.name}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={6}>
                  <Button
                    type="primary"
                    block
                    onClick={handleApplyLanguagesToAll}
                    disabled={!bulkLanguages.length || !(form.getFieldValue("joblocation") || []).length}
                  >
                    Apply to all districts
                  </Button>
                </Col>
              </Row>

              {form.getFieldValue("joblocation")?.length ? (
                form.getFieldValue("joblocation")?.map((loc: JobLocation) => (
                  <Row key={`${loc.district}-languages`} gutter={[16, 16]} style={{ marginBottom: "16px" }}>
                    <Col xs={24}>
                      <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
                        {loc.district}{loc.province ? ` (${loc.province})` : ""}
                      </div>
                      <Select
                        mode="tags"
                        style={{ width: "100%" }}
                        placeholder={loading ? "Loading..." : "Select or add languages"}
                        value={loc.languages || []}
                        onChange={(values) => {
                          const currentLocations = form.getFieldValue("joblocation") || [];
                          const updatedLocations = currentLocations.map((jobLoc: JobLocation) =>
                            jobLoc.district === loc.district ? { ...jobLoc, languages: values } : jobLoc
                          );
                          form.setFieldsValue({ joblocation: updatedLocations });
                          setFormData((prev) => ({ ...prev, joblocation: updatedLocations }));
                        }}
                      >
                        {languages.map((lang) => (
                          <Option key={lang.id} value={lang.name}>
                            {lang.name}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                  </Row>
                ))
              ) : (
                <div>Please add districts first</div>
              )}
            </Card>
          </Col>
        </Row>
      </Card>
    </Form>
  );
};

export default VacancyLocationCard;
